import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState } from 'react';
import type { Path, UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import FormButton from '@components/form/form-button';
import Image from '@domain/image';
import Layout from '@domain/layout';
import { useFormStore } from '@store/use-form-store';

import { useVismaConfiguration } from '../../services/visitor-management';
import type { RegisterFormData, Registrant } from '../../types';
import DataProtection from './data-protection';
import PreviousPageModal from './previous-page-modal';
import RegisterForm from './register-form';
import {
  RegisterPageContextProvider,
  useRegisterPage
} from './register-page-context';
import RegistrantList from './registrant-list';

const InlineRegistrantList = styled.div`
  display: none;

  @media only screen and (max-width: 1200px) {
    display: block;
    background: ${({ theme }) => theme.backgroundInput};
    border-radius: 12px;
    padding: 16px;
    margin: 16px 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 3em 0 5em 0;
  justify-content: space-between;
  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

interface RegisterPageContentProps {
  onValidSubmit: (data: RegisterFormData) => Promise<void>;
  onDataProtectionDecline: () => void;
  isSubmitting: boolean;
  onPreviousPage: () => void;
}

function RegisterPageContent({
  onValidSubmit: onValidSubmitProp,
  onDataProtectionDecline,
  isSubmitting,
  onPreviousPage
}: RegisterPageContentProps) {
  const { registrationType } = useFormStore();
  const { configuration } = useVismaConfiguration();
  const featuredImageId = configuration?.dataProtection?.featured_image?.id;
  const multi = configuration?.personalData?.multi;
  const {
    handleSubmit: rhfHandleSubmit,
    isValid,
    setError,
    loading,
    setLoading,
    registeredPersons,
    editRegistrantId,
    setEditRegistrantId,
    setFormField,
    clearForm,
    addRegistrant,
    updateRegistrant
  } = useRegisterPage()!;

  const [previousPageModal, setPreviousPageModal] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (editRegistrantId) {
      const el = document.getElementById('leftContainer');
      el?.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        (
          document.querySelector('input[name="fullname"]') as HTMLInputElement
        )?.focus();
      }, 300);
      const name =
        registeredPersons.find((p) => p.docNo === editRegistrantId)?.fullname ??
        '';
      toast.info(intl.formatMessage({ id: 'EDIT_REGISTRANT_INFO' }, { name }), {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'light'
      });
    }
  }, [editRegistrantId]);

  const scrollToBottom = () => {
    const el = document.getElementById('leftContainer');
    if (el && el.scrollHeight > el.clientHeight) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  const clearState = () => {
    clearForm();
    setTimeout(scrollToBottom, 300);
  };

  const onValidSubmit = async (data: RegisterFormData) => {
    const {
      fullname,
      mothersName,
      dateOfBirth,
      documentType,
      docNo,
      docExpiryDate,
      phoneNumber,
      companyName,
      expiry
    } = data;

    if (registrationType === 'multiple') {
      const existingPerson = registeredPersons.find((p) => p.docNo === docNo);

      if (existingPerson) {
        if (editRegistrantId === docNo) {
          updateRegistrant({
            fullname,
            mothersName,
            dateOfBirth,
            documentType,
            docNo,
            docExpiryDate,
            phoneNumber,
            companyName,
            expiry
          });
          setEditRegistrantId(null);
          clearState();
          return;
        }
        setError('docNo', {
          message: intl.formatMessage({
            id: 'PERSON_ALREADY_REGISTERED_ERROR'
          })
        });
        return;
      }

      if (registeredPersons.length >= 5) {
        toast.error(intl.formatMessage({ id: 'MAX_REGISTRANTS_ERROR' }), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'light'
        });
        return;
      }

      addRegistrant({
        fullname,
        mothersName,
        dateOfBirth,
        documentType,
        docNo,
        docExpiryDate,
        phoneNumber,
        companyName,
        expiry
      });
      toast.success(
        `${intl.formatMessage({ id: 'PERSON_ADDED' }, { name: fullname })}`,
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light'
        }
      );
      clearState();
      return;
    }

    // single registration — delegate to route screen's action
    try {
      await onValidSubmitProp(data);
    } catch {}
  };

  const handleClickNext = () => {
    if (loading) return;
    setLoading(true);
    rhfHandleSubmit(onValidSubmit)();
    setLoading(false);
    setTimeout(scrollToBottom, 300);
  };

  const handleClickNextNoScroll = () => {
    if (loading) return;
    setLoading(true);
    rhfHandleSubmit(onValidSubmit)();
    setLoading(false);
  };

  const handlePrevious = () => {
    onPreviousPage();
  };

  const checkButtonDisabled = () => {
    if (loading || isSubmitting) return true;
    if (!isValid) return true;
    if (registrationType === 'multiple' && registeredPersons.length >= 5)
      return true;
    return false;
  };

  const rightPanel =
    registrationType === 'multiple' ? (
      <RegistrantList handleAddRegistrant={handleClickNext} />
    ) : featuredImageId ? (
      <Image
        id={String(featuredImageId)}
        alt="banner"
        style={{ height: '100%', width: '100%', objectFit: 'contain' }}
      />
    ) : null;

  return (
    <Spin spinning={isSubmitting} size="large">
      <Layout rightPanel={rightPanel}>
        <PreviousPageModal
          open={previousPageModal}
          closeModal={() => {
            setPreviousPageModal(false);
          }}
          onProceed={handlePrevious}
        />
        <DataProtection onDecline={onDataProtectionDecline} />
        <RegisterForm />
        <ButtonContainer>
          <FormButton
            withLeftArrow
            variant="outlined"
            style={{ marginRight: '1em' }}
            onClick={() => {
              setPreviousPageModal(true);
            }}
          >
            {configuration?.generalInformation?.button_cancel}
          </FormButton>
          {process.env.NODE_ENV === 'development' && (
            <FormButton
              variant="outlined"
              style={{ marginRight: '1em' }}
              onClick={() => {
                setFormField('fullname', 'John Doe');
                setFormField('mothersName', 'Jane Doe');
                setFormField('dateOfBirth', dayjs('1990-05-15'));
                setFormField('documentType', 'passport');
                setFormField('docNo', 'AB123456');
                setFormField('docExpiryDate', dayjs('2030-01-01'));
                setFormField('phoneNumber', '+1234567890');
                setFormField('companyName', 'Acme Corp');
              }}
            >
              Fill Test Data
            </FormButton>
          )}
          <FormButton
            type="primary"
            withRightArrow
            onClick={handleClickNext}
            disabled={checkButtonDisabled()}
          >
            {editRegistrantId
              ? intl.formatMessage({ id: 'UPDATE_THE_PERSON' })
              : registrationType === 'multiple'
                ? registeredPersons.length === 0
                  ? multi?.button_add_person
                  : multi?.button_add_new_person
                : configuration?.generalInformation?.button_next}
          </FormButton>
        </ButtonContainer>
        {registrationType === 'multiple' && (
          <InlineRegistrantList>
            <RegistrantList handleAddRegistrant={handleClickNextNoScroll} />
          </InlineRegistrantList>
        )}
      </Layout>
    </Spin>
  );
}

interface RegisterFeatureProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods: UseFormReturn<any, any, any>;
  loading: boolean;
  setLoading: (v: boolean) => void;
  setFormField: (field: Path<RegisterFormData>, value: unknown) => void;
  setRegistrantInfo: (registrant: RegisterFormData) => void;
  clearForm: () => void;
  registeredPersons: Registrant[];
  editRegistrantId: string | null;
  setEditRegistrantId: (id: string | null) => void;
  addRegistrant: (registrant: Registrant) => void;
  removeRegistrant: (docNo: string) => void;
  updateRegistrant: (registrant: Registrant) => void;
  onValidSubmit: (data: RegisterFormData) => Promise<void>;
  onDataProtectionDecline: () => void;
  isSubmitting: boolean;
  onPreviousPage: () => void;
}

function RegisterFeature({
  methods,
  loading,
  setLoading,
  setFormField,
  setRegistrantInfo,
  clearForm,
  registeredPersons,
  editRegistrantId,
  setEditRegistrantId,
  addRegistrant,
  removeRegistrant,
  updateRegistrant,
  onValidSubmit,
  onDataProtectionDecline,
  isSubmitting,
  onPreviousPage
}: RegisterFeatureProps) {
  return (
    <RegisterPageContextProvider
      methods={methods}
      loading={loading}
      setLoading={setLoading}
      setFormField={setFormField}
      setRegistrantInfo={setRegistrantInfo}
      clearForm={clearForm}
      registeredPersons={registeredPersons}
      editRegistrantId={editRegistrantId}
      setEditRegistrantId={setEditRegistrantId}
      addRegistrant={addRegistrant}
      removeRegistrant={removeRegistrant}
      updateRegistrant={updateRegistrant}
    >
      <RegisterPageContent
        onValidSubmit={onValidSubmit}
        onDataProtectionDecline={onDataProtectionDecline}
        isSubmitting={isSubmitting}
        onPreviousPage={onPreviousPage}
      />
    </RegisterPageContextProvider>
  );
}

export default RegisterFeature;
